import type { CSSProperties, PropsWithChildren, ReactNode } from 'react'

export function Container({
  children,
  style,
  className,
}: PropsWithChildren<{ style?: CSSProperties; className?: string }>) {
  return (
    <div
      className={className}
      style={{
        maxWidth: 1200,
        margin: '0 auto',
        paddingLeft: 32,
        paddingRight: 32,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

export function Eyebrow({
  children,
  style,
}: PropsWithChildren<{ style?: CSSProperties }>) {
  return (
    <div
      style={{
        fontFamily: 'var(--mono)',
        fontSize: 12,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: 'var(--muted)',
        ...style,
      }}
    >
      {children}
    </div>
  )
}

type ButtonProps = {
  children: ReactNode
  variant?: 'primary' | 'accent' | 'ghost'
  size?: 'md' | 'lg'
  onClick?: () => void
  style?: CSSProperties
  className?: string
}

export function Button({
  children,
  variant = 'primary',
  onClick,
  style,
  size = 'md',
  className,
}: ButtonProps) {
  const base: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: size === 'lg' ? '16px 24px' : '12px 18px',
    fontSize: size === 'lg' ? 15 : 14,
    fontWeight: 500,
    borderRadius: 2,
    cursor: 'pointer',
    border: '1px solid transparent',
    transition: 'all 150ms ease',
    letterSpacing: '-0.005em',
    fontFamily: 'var(--sans)',
  }
  const variants: Record<NonNullable<ButtonProps['variant']>, CSSProperties> = {
    primary: { background: 'var(--fg)', color: 'var(--bg)', borderColor: 'var(--fg)' },
    accent: { background: 'var(--accent)', color: '#fff', borderColor: 'var(--accent)' },
    ghost: {
      background: 'transparent',
      color: 'var(--fg)',
      borderColor: 'var(--line-strong)',
    },
  }
  const variantClass = `btn-${variant}`
  return (
    <button
      type="button"
      onClick={onClick}
      className={className ? `${variantClass} ${className}` : variantClass}
      style={{ ...base, ...variants[variant], ...style }}
    >
      {children}
    </button>
  )
}

export function Wordmark({ name = 'Vello' }: { name?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        aria-hidden="true"
        style={{ flexShrink: 0 }}
      >
        <rect x="0.5" y="0.5" width="19" height="19" rx="2" fill="var(--fg)" />
        <path
          d="M5 5.5 L10 14.5 L15 5.5"
          stroke="var(--bg)"
          strokeWidth="1.8"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="16.5" cy="4" r="2" fill="var(--accent)" />
      </svg>
      <span style={{ fontSize: 17, fontWeight: 500, letterSpacing: '-0.025em' }}>
        {name}
      </span>
    </div>
  )
}

export function PortraitPlaceholder({
  label,
  tone = 'light',
  aspect = '3/4',
  radius = 2,
}: {
  label: string
  tone?: 'light' | 'dark'
  aspect?: string
  radius?: number
}) {
  const isDark = tone === 'dark'
  const bg = isDark ? '#1A1A17' : '#E8E4DA'
  const stripe = isDark ? 'rgba(255,255,255,0.035)' : 'rgba(10,10,10,0.045)'
  const fg = isDark ? 'rgba(245,245,240,0.55)' : 'rgba(26,26,20,0.55)'
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: aspect,
        background: `repeating-linear-gradient(135deg, ${bg} 0 12px, ${stripe} 12px 13px), ${bg}`,
        borderRadius: radius,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          bottom: 10,
          left: 12,
          fontFamily: 'var(--mono)',
          fontSize: 10,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: fg,
        }}
      >
        {label}
      </div>
    </div>
  )
}

export function hexToRgba(hex: string, alpha: number) {
  const h = hex.replace('#', '')
  const r = parseInt(h.substring(0, 2), 16)
  const g = parseInt(h.substring(2, 4), 16)
  const b = parseInt(h.substring(4, 6), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

export function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

export async function startCheckout(plan: 'launch' | 'plus') {
  try {
    const res = await fetch('/api/stripe/checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan }),
    })
    const data = (await res.json()) as { url?: string; error?: string }
    if (!res.ok || !data.url) {
      throw new Error(data.error ?? `Checkout failed (${res.status})`)
    }
    window.location.assign(data.url)
  } catch (error) {
    console.error('Checkout error:', error)
    window.alert(
      'Could not start checkout. Email hello@withvello.com and it will get sorted.',
    )
  }
}
